<template>
  <div class="common-card">
    <modal-invoice-filter
      :filter_invoice_type_id.sync="filter_invoice_type_id"
      :filter_project_id.sync="filter_project_id"
      :filter_customer_id.sync="filter_customer_id"
      :filter_product_id.sync="filter_product_id"
      :filter_quotation_id.sync="filter_quotation_id"
      :filter_payment_status.sync="filter_payment_status"
      :filter_start_date.sync="filter_start_date"
      :filter_end_date.sync="filter_end_date"
      :filter_min_amount.sync="filter_min_amount"
      :filter_max_amount.sync="filter_max_amount"
      :invoiceTypeOptions="invoiceTypeOptions"
      :projectOptions="projectOptions"
      :customerOptions="customerOptions"
      :productOptions="productOptions"
      :quotationOptions="handleAdminQuotationOptions"
      @refetch-data="refetchData"
    />

    <b-card class="top mb-0">
      <div class="d-flex flex-wrap align-items-center justify-content-between">
        <div class="d-flex mb-1 mb-sm-0 align-items-center">
          <h4 class="font-weight-bolder ml-1 text-nowrap mb-0">
            <font-awesome-icon
              class="mr-50"
              icon="fa-solid fa-file-invoice-dollar"
            />
            發票資料
          </h4>
          <div class="d-flex ml-3 align-items-center">
            <label>顯示</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>條目</label>
          </div>
        </div>
        <div class="d-flex flex-wrap mobile-w100 mt-1 mt-lg-0">
          <div class="d-flex align-items-center mr-2 mb-1 mb-sm-0">
            <input
              id="is_open_in_new_window"
              v-model="openInNewWindow"
              type="checkbox"
              class="mr-25"
            />
            <label for="is_open_in_new_window">在新視窗編輯</label>
          </div>
          <b-form-input
            v-model="searchQuery"
            class="search-form mb-1 mb-sm-0 mr-sm-1"
            placeholder="搜尋發票"
          />
          <b-button
            variant="primary"
            class="mobile-w100 mr-sm-1 mb-1 mb-sm-0"
            @click="$bvModal.show('modal-invoice-filter')"
            ><feather-icon
              class="mr-1"
              icon="filterIcon"
              size="18"
            ></feather-icon
            >篩選</b-button
          >
          <!-- 匯出功能按鈕 -->
          <b-dropdown
            variant="success"
            text="匯出"
            class="mobile-w100 mr-sm-1 mb-1 mb-sm-0"
            :disabled="isExporting"
            v-if="ability.can('export', 'invoices')"
          >
            <template #button-content>
              <b-spinner v-if="isExporting" small class="mr-1"></b-spinner>
              <feather-icon v-else size="16" class="mr-1" icon="DownloadIcon" />
              {{ isExporting ? "匯出中..." : "匯出" }}
            </template>
            <b-dropdown-item @click="exportCurrentPage" :disabled="isExporting">
              <feather-icon size="16" class="mr-1" icon="FileTextIcon" />
              匯出此頁面
            </b-dropdown-item>
            <b-dropdown-item @click="exportAllRecords" :disabled="isExporting">
              <feather-icon size="16" class="mr-1" icon="DatabaseIcon" />
              匯出全部記錄
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            class="mobile-w100"
            :to="{ name: 'invoice-create' }"
            v-if="ability.can('create', 'invoices')"
          >
            <feather-icon class="mr-1" icon="plusIcon" size="18"></feather-icon
            >新增發票</b-button
          >
        </div>
      </div>
    </b-card>

    <b-card no-body class="card-statistics bottom">
      <b-card-body class="p-2">
        <b-table
          ref="refInvoiceListTable"
          class="position-relative"
          :items="fetchInvoices"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          empty-text="未找到匹配的記錄"
          :sort-desc.sync="isSortDirDesc"
        >
          <!-- Column: invoice_number -->
          <template #cell(invoice_number)="data">
            <div>
              {{ data.item.invoice_number }}
            </div>
            <label v-if="data.item.invoice_number_remark" class="text-muted">{{ data.item.invoice_number_remark }}</label>
          </template>

          <!-- Column: invoice_type -->
          <template #cell(invoice_type)="data">
            {{ data.item.invoice_type && data.item.invoice_type.name }}
          </template>

          <template #cell(quotation_id)="data">
            {{
              getQuotation(data.item.quotation_id) &&
              getQuotation(data.item.quotation_id).quotation_number
            }}
          </template>

          <template #cell(id)="data">
            {{ data.item.invoice_number }}
          </template>

          <template #cell(total_amount)="data">
            ${{
              data.item.total_amount &&
              Number(data.item.total_amount).toFixed(2)
            }}
          </template>
          <template #cell(total_unpaid)="data">
            <span class="colorRed"
              >${{
                data.item.total_amount &&
                data.item.total_paid &&
                (
                  Number(data.item.total_amount) - Number(data.item.total_paid)
                ).toFixed(2)
              }}</span
            >
          </template>

          <!-- Column: cost_items -->
          <template #cell(show_details)="row">
            <b-button
              v-if="row.item.cost_items"
              size="sm"
              @click="toggleItemDetails(row)"
              class="mr-2"
            >
              {{ getItemDetailsShowing(row.item.id) ? "隱藏" : "顯示" }}
            </b-button>
          </template>

          <!-- Column: payment_steps -->
          <template #cell(show_details2)="row">
            <b-button
              v-if="
                (row.item.payment_steps && row.item.payment_steps.length > 0) ||
                (row.item.paymentSteps && row.item.paymentSteps.length > 0)
              "
              size="sm"
              @click="togglePaymentStepsDetails(row)"
              class="mr-2"
              variant="primary"
            >
              {{ getPaymentStepsShowing(row.item.id) ? "隱藏" : "顯示" }}
            </b-button>
            <span v-else class="text-muted">還沒設置階段收費</span>
          </template>

          <template #row-details="row">
            <!-- 項目摘要詳情 -->
            <div v-if="getItemDetailsShowing(row.item.id)">
              <div
                class="info-container"
                v-if="row.item.salary_items && row.item.salary_items.length"
              >
                <b-row
                  class="p-2 backgroundColor5 mb-1"
                  v-for="(worker, index) in row.item.salary_items"
                  :key="`cost${index}`"
                >
                  <b-col cols="12" md="3">
                    <div class="row mb-50">
                      <b class="label">師傅：</b>
                      <span>{{ getWorkerName(worker.worker_id) }}</span>
                    </div>
                    <div class="row mb-50">
                      <b class="label">職位：</b>
                      <span>{{ worker.position }}</span>
                    </div>
                    <div class="row mb-50">
                      <b class="label">工資：</b>
                      <span
                        >{{ worker.working_days }}天 * ${{
                          Number(worker.day_salary).toFixed(2)
                        }}每日 = ${{
                          Number(worker.total_salary).toFixed(2)
                        }}</span
                      >
                    </div>
                  </b-col>
                  <b-col cols="12" md="3">
                    <div class="row mb-50">
                      <b class="label">開工日期：</b>
                      <pre>{{ worker.working_date }}</pre>
                    </div>
                  </b-col>
                </b-row>
              </div>
              <div
                class="info-container"
                v-if="row.item.cost_items && row.item.cost_items.length"
              >
                <b-row
                  class="p-2 backgroundColor3 mb-1"
                  v-for="(cost, index) in row.item.cost_items"
                  :key="`cost${index}`"
                >
                  <b-col cols="12" md="3">
                    <div class="row mb-50">
                      <b class="label">商品分類：</b>
                      <span>{{
                        getProductCategory(cost.product_category_id) &&
                        getProductCategory(cost.product_category_id).name
                      }}</span>
                    </div>

                    <div class="row mb-50">
                      <b class="label">商品：</b>
                      <span>{{
                        getProduct(cost.product_id) &&
                        getProduct(cost.product_id).product_name
                      }}</span>
                    </div>

                    <div class="row mb-50">
                      <b class="label">金額：</b>
                      <span
                        >{{ parseFloat(Number(cost.quantity).toString()) }}
                        {{ getProduct(cost.product_id).unit }}

                        * ${{ Number(cost.unit_price).toFixed(2) }} = ${{
                          Number(cost.amount).toFixed(2)
                        }}</span
                      >
                    </div>
                  </b-col>
                  <b-col cols="12" md="3">
                    <div class="row mb-50">
                      <b class="label">增加空行數量：</b>
                      <pre class="mb-0">{{ cost.empty_rows }}</pre>
                    </div>
                    <div class="row mb-50">
                      <b class="label">備註/尺寸：</b>
                      <span class="mb-0">{{ cost.item_description }}</span>
                    </div>
                  </b-col>
                </b-row>
              </div>
            </div>

            <!-- 階段收費詳情 -->
            <div
              v-if="getPaymentStepsShowing(row.item.id)"
              class="payment-steps-details"
            >
              <div class="info-container">
                <h6 class="mb-3 text-primary">
                  <feather-icon icon="CreditCardIcon" size="16" class="mr-1" />
                  階段收費詳情
                  <span v-if="row.item.payment_steps_title" class="text-muted">
                    - {{ row.item.payment_steps_title }}
                  </span>
                </h6>
                <div
                  v-if="
                    getPaymentStepsData(row.item) &&
                    getPaymentStepsData(row.item).length > 0
                  "
                >
                  <b-row
                    class="p-1 mb-1 backgroundColor2 border-left-primary"
                    v-for="(step, index) in getPaymentStepsData(row.item)"
                    :key="`payment_step_${index}`"
                  >
                    <b-col cols="12" class="d-flex">
                      <div class="d-flex mr-2">
                        <b class="label">階段：</b>
                        <span>{{ step.step_phase }}</span>
                      </div>

                      <div class="d-flex mr-2">
                        <b class="label">佔比：</b>
                        <span class="text-info font-weight-bold"
                          >{{ step.step_percentage }}%</span
                        >
                      </div>

                      <div class="d-flex mr-2">
                        <b class="label">金額：</b>
                        <span class="text-success font-weight-bold"
                          >${{ Number(step.step_amount).toFixed(2) }}</span
                        >
                      </div>

                      <div class="d-flex" v-if="step.step_name">
                        <b class="label">備註：</b>
                        <span>{{ step.step_name }}</span>
                      </div>
                    </b-col>
                  </b-row>
                  <b-row class="p-1 mb-1 backgroundColor2 border-left-primary">
                    <small class="text-info">
                      <strong>總計：</strong>
                      {{
                        getPaymentStepsData(row.item)
                          .reduce(
                            (sum, step) => sum + Number(step.step_percentage),
                            0
                          )
                          .toFixed(2)
                      }}% = ${{
                        getPaymentStepsData(row.item)
                          .reduce(
                            (sum, step) => sum + Number(step.step_amount),
                            0
                          )
                          .toFixed(2)
                      }}
                    </small>
                  </b-row>
                </div>
                <div v-else class="text-center text-muted py-3">
                  <feather-icon icon="InfoIcon" size="16" class="mr-1" />
                  此發票沒有設置階段收費信息
                </div>
              </div>
            </div>
          </template>

          <!-- Column: stamp -->
          <template #cell(stamp)="data">
            <feather-icon
              v-if="data.item.stamp_id"
              icon="CheckIcon"
              size="18"
              class="mr-50 text-success"
            />
          </template>

          <template #cell(customer.name_cn)="data">
            <a
              v-if="
                ability.can('view_detail', 'setting_customers') &&
                ((data.item.customer && data.item.customer.id) || data.item.customer_id)
              "
              :href="
                $router.resolve({
                  name: 'customer-view',
                  params: {
                    id:
                      data.item.customer && data.item.customer.id
                        ? data.item.customer.id
                        : data.item.customer_id,
                  },
                }).href
              "
              class="text-primary"
            >
              {{ data.value }}
            </a>
            <span v-else>{{ data.value }}</span>
          </template>

          <template #cell(created_at)="data">
            {{ getDateTime(data.item.created_at) }}
          </template>

          <template #cell(actions)="data">
            <b-dropdown
              :class="`action-trigger-btn ${
                invoicesList.length <= 3 ? 'hor' : 'ver'
              }`"
              :right="$store.state.appConfig.isRTL"
            >
              <template #button-content>
                <feather-icon
                  icon="EyeIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>

              <b-dropdown-item
                v-if="ability.can('print', 'invoices')"
                :to="{ name: 'invoices-preview', params: { id: data.item.id } }"
              >
                <feather-icon icon="FileTextIcon" />
                <span class="align-middle ml-50">列印發票</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="ability.can('update', 'invoices')"
                @click="handleEditClick(data.item.id)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">修改資料</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="ability.can('delete', 'invoices')"
                @click="removeItem(data.item.id)"
              >
                <feather-icon icon="Trash2Icon" />
                <span class="align-middle ml-50">刪除記錄</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="ability.can('create', 'receipts')"
                :to="{
                  name: 'receipt-create',
                  query: { invoice_id: data.item.id },
                }"
              >
                <feather-icon icon="BookIcon" />
                <span class="align-middle ml-50">生成收據</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="ability.can('create', 'invoices')"
                :to="{
                  name: 'invoice-copy',
                  params: { id: data.item.id },
                }"
              >
                <feather-icon icon="CopyIcon" />
                <span class="align-middle ml-50">複製發票</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted"
                >在{{ dataMeta.of }}個記錄中，正在顯示 {{ dataMeta.from }} 至
                {{ dataMeta.to }}個記錄</span
              >
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRow"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>
    
<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import store from "@/store";
import { ref, onUnmounted, watch } from "@vue/composition-api";
import useInvoicesList from "./useInvoicesList";
import invoiceStoreModule from "../invoiceStoreModule";
import ModalInvoiceFilter from "../modal/ModalInvoiceFilter.vue";
// import ModalPrintInvoice from "../modal/ModalPrintInvoice.vue";

export default {
  components: {
    ModalInvoiceFilter,
    BCard,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    // ModalPrintInvoice,
    vSelect,
  },
  data() {
    return {
      // 獨立管理兩種詳情顯示狀態
      detailsShowing: {}, // 項目摘要詳情顯示狀態 { invoiceId: boolean }
      paymentStepsShowing: {}, // 階段收費詳情顯示狀態 { invoiceId: boolean }
      isExporting: false, // 匯出狀態
    };
  },
  computed: {
    handleAdminQuotationOptions() {
      // if (this.ability.can("admin", "invoices")) {
      //   return this.quotationOptions;
      // } 
        if (!this.invoicesList || !this.invoicesList.length) {
          return [];
        }
         const referencedQuotationIds = this.invoicesList
          .map((item) => item.quotation_id)
          .filter((id) => id !== null && id !== undefined);

        // 去重
        const uniqueQuotationIds = [...new Set(referencedQuotationIds)];

        // 從 quotationOptions 中篩選出被引用報價單
        return this.quotationOptions.filter((item) =>
          uniqueQuotationIds.includes(item.id)
        );
      
    },
  }, 
  methods: {
    handleEditClick(id) {
      const editRoute = this.$router.resolve({
        name: "invoice-edit",
        params: { id: id },
      });
      if (this.openInNewWindow) {
        // 在新視窗中開啟
        window.open(editRoute.href, "_blank");
      } else {
        // 在當前頁面中導航
        this.$router.push({
          name: "invoice-edit",
          params: { id: id },
        });
      }
    },
    // printInvoice(item){
    //   this.printData = item
    //   this.$bvModal.show('modal-print-invoice')
    // },

    // 項目摘要詳情管理方法
    toggleItemDetails(row) {
      const invoiceId = row.item.id;
      const wasPaymentStepsShowing = this.paymentStepsShowing[invoiceId];
      const wasItemDetailsShowing = this.detailsShowing[invoiceId];

      // 關閉階段收費詳情（如果開啟的話）
      this.$set(this.paymentStepsShowing, invoiceId, false);
      // 切換項目摘要詳情
      this.$set(this.detailsShowing, invoiceId, !wasItemDetailsShowing);

      // 只有在需要時才調用 row.toggleDetails()
      if (!wasPaymentStepsShowing && !wasItemDetailsShowing) {
        // 如果之前兩個都沒有顯示，需要打開 row details
        row.toggleDetails();
      } else if (
        !this.detailsShowing[invoiceId] &&
        !this.paymentStepsShowing[invoiceId]
      ) {
        // 如果現在兩個都沒有顯示，需要關閉 row details
        row.toggleDetails();
      }
    },

    getItemDetailsShowing(invoiceId) {
      return !!this.detailsShowing[invoiceId];
    },

    // 階段收費詳情管理方法
    togglePaymentStepsDetails(row) {
      const invoiceId = row.item.id;
      const wasItemDetailsShowing = this.detailsShowing[invoiceId];
      const wasPaymentStepsShowing = this.paymentStepsShowing[invoiceId];

      // 關閉項目摘要詳情（如果開啟的話）
      this.$set(this.detailsShowing, invoiceId, false);
      // 切換階段收費詳情
      this.$set(this.paymentStepsShowing, invoiceId, !wasPaymentStepsShowing);

      // 只有在需要時才調用 row.toggleDetails()
      if (!wasItemDetailsShowing && !wasPaymentStepsShowing) {
        // 如果之前兩個都沒有顯示，需要打開 row details
        row.toggleDetails();
      } else if (
        !this.detailsShowing[invoiceId] &&
        !this.paymentStepsShowing[invoiceId]
      ) {
        // 如果現在兩個都沒有顯示，需要關閉 row details
        row.toggleDetails();
      }
    },

    getPaymentStepsShowing(invoiceId) {
      return !!this.paymentStepsShowing[invoiceId];
    },

    // 獲取階段收費數據（兼容不同的數據格式）
    getPaymentStepsData(item) {
      // 優先使用 paymentSteps（後端關聯名稱），其次使用 payment_steps
      return item.paymentSteps || item.payment_steps || [];
    },
    getProject(id) {
      if (!id || !this.projectOptions || !this.projectOptions.length) {
        return null;
      }
      return this.projectOptions.find(function (ele) {
        return ele.id === id;
      });
    },

    getProductCategory(id) {
      if (
        !id ||
        !this.productCategoriesOptions ||
        !this.productCategoriesOptions.length
      ) {
        return null;
      }
      return this.productCategoriesOptions.find(function (ele) {
        return ele.id === id;
      });
    },

    getProduct(id) {
      if (!id || !this.productOptions || !this.productOptions.length) {
        return { unit: "次" };
      }
      const product = this.productOptions.find(function (ele) {
        return ele.id === id;
      });
      return { ...{ unit: "次" }, ...product };
    },

    getQuotation(id) {
      if (!id || !this.quotationOptions || !this.quotationOptions.length) {
        return null;
      }
      return this.quotationOptions.find(function (ele) {
        return ele.id === id;
      });
    },

    getWorkerName(workerId) {
      if (!workerId || !this.workerOptions || !this.workerOptions.length) {
        return "";
      }
      const worker = this.workerOptions.find(function (ele) {
        return ele.id === workerId;
      });
      return worker ? worker.name_cn : "";
    },

    removeItem(id) {
      // check number of record
      this.$swal({
        title: "你確定嗎?",
        text: "您的決定是最終決定，您將無法取回這記錄。",
        showCancelButton: true,
        confirmButtonText: "刪除",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch("invoice/deleteInvoice", { id })
            .then((response) => {
              this.$swal({
                text: response.data.message,
                icon: "success",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
              this.refetchData();
            })
            .catch((error) => {
              this.$swal({
                text: error.response.data.message,
                icon: "error",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
            });
        }
      });
    },

    // 匯出此頁面
    async exportCurrentPage() {
      if (this.isExporting) return;

      this.isExporting = true;

      try {
        const exportParams = {
          q: this.searchQuery,
          invoice_type_id: this.filter_invoice_type_id,
          quotation_id: this.filter_quotation_id,
          project_id: this.filter_project_id,
          customer_id: this.filter_customer_id,
          product_id: this.filter_product_id,
          payment_status: this.filter_payment_status,
          start_date: this.filter_start_date,
          end_date: this.filter_end_date,
          min_amount: this.filter_min_amount,
          max_amount: this.filter_max_amount,
          export_type: "current",
          page: this.currentPage,
          perPage: this.perPage,
        };

        const response = await this.$store.dispatch(
          "invoice/exportInvoices",
          exportParams
        );

        // 生成動態文件名，包含匯出日期
        const currentDate = new Date().toISOString().split("T")[0]; // YYYY-MM-DD 格式
        const fileName = `發票資料_當前頁面_${currentDate}.xlsx`;

        // 創建下載連結
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        // 顯示成功提示
        this.$bvToast.toast("當前頁面 Excel 檔案已成功下載", {
          title: "匯出成功",
          variant: "success",
          solid: true,
          autoHideDelay: 3000,
        });
      } catch (error) {
        console.error("匯出當前頁面失敗:", error);

        // 顯示錯誤提示
        this.$bvToast.toast(
          error.response?.data?.message ||
            "匯出當前頁面過程中發生錯誤，請稍後再試",
          {
            title: "匯出失敗",
            variant: "danger",
            solid: true,
            autoHideDelay: 5000,
          }
        );
      } finally {
        this.isExporting = false;
      }
    },

    // 匯出全部記錄
    async exportAllRecords() {
      if (this.isExporting) return;

      this.isExporting = true;

      try {
        const exportParams = {
          q: this.searchQuery,
          invoice_type_id: this.filter_invoice_type_id,
          quotation_id: this.filter_quotation_id,
          project_id: this.filter_project_id,
          customer_id: this.filter_customer_id,
          product_id: this.filter_product_id,
          payment_status: this.filter_payment_status,
          start_date: this.filter_start_date,
          end_date: this.filter_end_date,
          min_amount: this.filter_min_amount,
          max_amount: this.filter_max_amount,
          export_type: "all",
        };

        const response = await this.$store.dispatch(
          "invoice/exportInvoices",
          exportParams
        );

        // 生成動態文件名，包含匯出日期
        const currentDate = new Date().toISOString().split("T")[0]; // YYYY-MM-DD 格式
        const fileName = `發票資料_全部記錄_${currentDate}.xlsx`;

        // 創建下載連結
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        // 顯示成功提示
        this.$bvToast.toast("全部記錄 Excel 檔案已成功下載", {
          title: "匯出成功",
          variant: "success",
          solid: true,
          autoHideDelay: 3000,
        });
      } catch (error) {
        console.error("匯出全部記錄失敗:", error);

        // 顯示錯誤提示
        this.$bvToast.toast(
          error.response?.data?.message ||
            "匯出全部記錄過程中發生錯誤，請稍後再試",
          {
            title: "匯出失敗",
            variant: "danger",
            solid: true,
            autoHideDelay: 5000,
          }
        );
      } finally {
        this.isExporting = false;
      }
    },
  },
  setup() {
    const USER_APP_STORE_MODULE_NAME = "invoice";

    // Register module
    if (!store.hasModule(USER_APP_STORE_MODULE_NAME))
      store.registerModule(USER_APP_STORE_MODULE_NAME, invoiceStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(USER_APP_STORE_MODULE_NAME))
        store.unregisterModule(USER_APP_STORE_MODULE_NAME);
    });

    const {
      fetchInvoices,
      tableColumns,
      perPage,
      currentPage,
      totalRow,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      invoicesList,
      refInvoiceListTable,
      ability,
      refetchData,
      filter_payment_status,
      filter_start_date,
      filter_end_date,
      filter_min_amount,
      filter_max_amount,
      filter_invoice_type_id,
      filter_project_id,
      filter_customer_id,
      filter_product_id,
      filter_quotation_id,
      invoiceTypeOptions,
      quotationOptions,
      customerOptions,
      productOptions,
      workerOptions,
      projectOptions,
      productCategoriesOptions,
    } = useInvoicesList();

    const openInNewWindow = ref(
      localStorage.getItem("invoice-edit-new-window") === "true"
    );
    // 監聽 checkbox 變化並保存到 localStorage
    watch(openInNewWindow, (newValue) => {
      localStorage.setItem("invoice-edit-new-window", newValue.toString());
    });

    return {
      fetchInvoices,
      tableColumns,
      perPage,
      currentPage,
      totalRow,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      invoicesList,
      refInvoiceListTable,
      ability,
      refetchData,
      filter_payment_status,
      filter_start_date,
      filter_end_date,
      filter_min_amount,
      filter_max_amount,
      filter_invoice_type_id,
      filter_project_id,
      filter_customer_id,
      filter_product_id,
      filter_quotation_id,
      invoiceTypeOptions,
      quotationOptions,
      customerOptions,
      productOptions,
      workerOptions,
      projectOptions,
      productCategoriesOptions,
      openInNewWindow,
    };
  },
};
</script>
    
<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.payment-steps-details {
  .border-left-primary {
    border-left: 4px solid #7367f0 !important;
  }

  .label {
    min-width: 40px;
    display: inline-block;
  }
}
</style>
    
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@/assets/scss/variables/_variables.scss";
</style>
    